// Wizard Step 3 — Зураг + Refund + Тээвэр (screens-buyer-app §6).
//
// Photo upload (RETURN reason: >=1 required), preferred refund method (D2,
// optional hint), shipping responsibility (D11, SELLER_PICKS_UP vs BUYER_SHIPS
// with the loss → refund-0% warning). Refund method is sent as
// preferredRefundMethod; shipping is NOT sent on create (B66) — it is a request
// the supplier decides on when proposing. ETA texts come from the BE preview (B67).
import { Box, Group, SimpleGrid, Stack, Text, UnstyledButton } from "@mantine/core";
import { i18n } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import type { IRefundTimelinePreview, RefundMethod, ShippingResponsibility } from "../../../../apis/return";
import { REFUND_METHOD_META, refundEtaLabel, refundMethodLabelMn } from "../constants";
import { WizardPhotoUploader } from "./WizardPhotoUploader";
import classes from "./wizard-stepper.module.css";

const PICKUP_SLA_DAYS = 14; // D11 default

const REFUND_ORDER: RefundMethod[] = ["BANK_TRANSFER", "CREDIT_BALANCE", "INVOICE_DEDUCTION", "CASH"];

interface ShippingOpt {
  value: ShippingResponsibility;
  labelMn: string;
  descMn: string;
  warn?: string;
}

const shippingOpts = (): ShippingOpt[] => [
  {
    value: "SELLER_PICKS_UP",
    labelMn: i18n.t("buyerApp:stepPhotos.shipping.sellerPicksUpLabel"),
    descMn: i18n.t("buyerApp:stepPhotos.shipping.sellerPicksUpDesc", { days: PICKUP_SLA_DAYS }),
  },
  {
    value: "BUYER_SHIPS",
    labelMn: i18n.t("buyerApp:stepPhotos.shipping.buyerShipsLabel"),
    descMn: i18n.t("buyerApp:stepPhotos.shipping.buyerShipsDesc"),
    warn: i18n.t("buyerApp:stepPhotos.shipping.buyerShipsWarn"),
  },
];

interface Props {
  requestType: "RETURN" | "SIMPLE";
  photos: string[];
  refundMethod: RefundMethod | null;
  shipping: ShippingResponsibility | null;
  photoError?: string | null;
  /** B67: BE refund-timeline previews keyed by refund method (undefined while loading). */
  refundEta?: Partial<Record<string, IRefundTimelinePreview>>;
  onPhotos: (urls: string[]) => void;
  onRefundMethod: (method: RefundMethod | null) => void;
  onShipping: (shipping: ShippingResponsibility) => void;
}

/** Canvas card: grey icon box (refund channel) or a radio dot (shipping); chosen = 2px dark ring. */
function SelectCard({
  active,
  onSelect,
  icon,
  title,
  desc,
}: {
  active: boolean;
  onSelect: () => void;
  icon?: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <UnstyledButton className={classes.reason} role="radio" aria-checked={active} onClick={onSelect} data-centered={icon ? true : undefined}>
      {icon ? (
        <Box component="span" className={classes.iconBox} aria-hidden>
          {icon}
        </Box>
      ) : (
        <Box component="span" className={classes.radio} aria-hidden />
      )}
      <Box miw={0}>
        <Text className={classes.reasonLabel}>{title}</Text>
        <Text className={classes.reasonHint}>{desc}</Text>
      </Box>
    </UnstyledButton>
  );
}

export function StepPhotosRefund({
  requestType,
  photos,
  refundMethod,
  shipping,
  photoError,
  refundEta,
  onPhotos,
  onRefundMethod,
  onShipping,
}: Props) {
  const { t } = useTranslation("buyerApp");
  const photosRequired = requestType === "RETURN";

  return (
    <Stack gap={22}>
      {/* ── Photos ── */}
      <Stack gap={10}>
        <Group gap={10} align="baseline">
          <Text className={classes.title}>{t("stepPhotos.evidenceTitle")}</Text>
          {photosRequired ? <Text className={classes.subtitle}>{t("stepPhotos.photoRequired")}</Text> : null}
        </Group>
        <WizardPhotoUploader value={photos} onChange={onPhotos} error={photoError} />
      </Stack>

      {/* ── Refund method (D2, optional) ── */}
      <Stack gap={10}>
        <Box>
          <Text className={classes.title}>{t("stepPhotos.refundChannelTitle")}</Text>
          <Text className={classes.subtitle}>{t("stepPhotos.refundHint")}</Text>
        </Box>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={10} role="radiogroup" aria-label={t("stepPhotos.refundChannelAria")}>
          {REFUND_ORDER.map((m) => {
            const Icon = REFUND_METHOD_META[m].Icon;
            return (
              <SelectCard
                key={m}
                active={refundMethod === m}
                onSelect={() => onRefundMethod(refundMethod === m ? null : m)}
                icon={<Icon size={18} />}
                title={refundMethodLabelMn(m)}
                desc={refundEtaLabel(refundEta?.[m])}
              />
            );
          })}
        </SimpleGrid>
      </Stack>

      {/* ── Shipping responsibility (D11) ── */}
      <Stack gap={10}>
        <Text className={classes.title}>{t("stepPhotos.shippingTitle")}</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={10} role="radiogroup" aria-label={t("stepPhotos.shippingAria")}>
          {shippingOpts().map((opt) => (
            <SelectCard
              key={opt.value}
              active={shipping === opt.value}
              onSelect={() => onShipping(opt.value)}
              title={opt.labelMn}
              desc={opt.descMn}
            />
          ))}
        </SimpleGrid>
        {shipping === "BUYER_SHIPS" ? (
          <Box className={classes.warn} role="note">
            <Box component="span" className={classes.warnDot} aria-hidden />
            <Text className={classes.warnText}>{t("stepPhotos.buyerShipsWarning")}</Text>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
}
